<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button @click.prevent="home" class="sci-btn-back" title="Torna al Gestionale">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <div class="sci-badge-tag">
            <i class="fas fa-calendar-alt"></i> Sala & Comande
          </div>
          <h1 class="sci-view-title">{{ $t('prenotazioniSetting', 'Configurazione Prenotazioni Sale') }}</h1>
          <p class="sci-view-subtitle">Impostazione capienza coperti, fasce orarie servizio e sistema avvisi acustici</p>
        </div>
      </div>

      <div class="sci-header-actions">
        <button 
          type="button" 
          class="sci-btn sci-btn-warning btn-touch test-sound-btn" 
          @click="testNotificationSound"
          title="Riproduce l'audio che avviserà la sala in caso di nuova prenotazione per oggi"
        >
          <i class="fas fa-volume-up me-2"></i>{{ $t('test_suono', 'Test Notifica Audio') }}
        </button>
        <button 
          type="button" 
          class="sci-btn sci-btn-primary btn-touch" 
          @click="saveAllSettings"
          :disabled="isSavingAll"
        >
          <i class="fas fa-save me-2"></i>{{ isSavingAll ? $t('salvataggio', 'Salvataggio...') : $t('salva_tutto', 'Salva Tutto') }}
        </button>
      </div>
    </header>

    <!-- Bento KPI Summary Grid -->
    <section class="sci-kpi-grid">
      <div class="sci-kpi-card primary">
        <div class="kpi-icon-wrap blue">
          <i class="fas fa-door-open"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Sale Configurate</span>
          <span class="kpi-value">{{ settings.length }}</span>
          <span class="kpi-sub">Aree di somministrazione</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap green">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Sale con Prenotazioni Attive</span>
          <span class="kpi-value text-success">{{ activeRoomsCount }}</span>
          <span class="kpi-sub">Aperte alle prenotazioni online</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap purple">
          <i class="fas fa-users"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Capienza Complessiva</span>
          <span class="kpi-value">{{ totalCapacity }}</span>
          <span class="kpi-sub">Massimo coperti simultanei</span>
        </div>
      </div>
    </section>

    <!-- Alert Informativo Scientifico / Cognitive Reassurance -->
    <div class="sci-card info-banner">
      <div class="info-banner-icon">
        <i class="fas fa-bell"></i>
      </div>
      <div class="info-banner-content">
        <h4 class="info-banner-title">Sistema Notifiche Audio Real-Time</h4>
        <p class="info-banner-desc">
          Quando una sala ha gli opportuni orari configurati e arriva una prenotazione per il <strong>giorno stesso</strong> e per il <strong>servizio attuale (pranzo o cena)</strong>, il gestionale emetterà un <strong>allarme acustico in qualsiasi schermata</strong> aperta per allertare tempestivamente lo staff.
        </p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Caricamento impostazioni...</span>
      </div>
      <p class="mt-2 text-muted">Caricamento sale e configurazioni in corso...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="settings.length === 0" class="sci-card empty-card text-center py-5">
      <i class="fas fa-door-closed fa-3x text-muted mb-3"></i>
      <h3>Nessuna Sala Trovata</h3>
      <p class="text-muted">Crea prima le sale nella sezione Sala per poterne configurare gli orari e la capienza.</p>
      <button class="sci-btn sci-btn-primary" @click="$router.push({ name: 'Sala' })">Vai a Gestione Sala</button>
    </div>

    <!-- Bento Spatial Grid delle Sale -->
    <div v-else class="settings-grid">
      <div 
        v-for="setting in settings" 
        :key="setting.codicesala" 
        class="room-card sci-card"
        :class="{ 'card-disabled': setting.attiva === false }"
      >
        <!-- Card Header -->
        <div class="room-card-header">
          <div class="room-title-box">
            <div class="room-icon-wrapper" :class="{ 'icon-active': setting.attiva !== false }">
              <i class="fas fa-utensils"></i>
            </div>
            <div>
              <h2 class="room-name">{{ setting.nomeSala || setting.codicesala }}</h2>
              <span class="status-pill" :class="setting.attiva !== false ? 'pill-active' : 'pill-inactive'">
                <span class="status-dot"></span>
                {{ setting.attiva !== false ? 'Prenotazioni Attive' : 'Prenotazioni Disabilitate' }}
              </span>
            </div>
          </div>

          <!-- Master Switch Sala -->
          <div class="master-switch-box">
            <label class="toggle-switch-label" :for="'toggle-sala-' + setting.codicesala">
              <span class="toggle-text">{{ setting.attiva !== false ? 'Abilitata' : 'Disabilitata' }}</span>
              <div class="sci-toggle">
                <input 
                  type="checkbox" 
                  :id="'toggle-sala-' + setting.codicesala" 
                  v-model="setting.attiva" 
                  @change="updateSetting(setting)"
                />
                <span class="sci-toggle-slider"></span>
              </div>
            </label>
          </div>
        </div>

        <!-- Card Body -->
        <div class="room-card-body">
          <!-- Sezione Capienza Coperti -->
          <div class="covers-section">
            <div class="covers-field">
              <label class="field-label">
                <i class="fas fa-users me-1 text-primary"></i> Max Coperti Totali Sala
              </label>
              <div class="stepper-input">
                <button type="button" class="stepper-btn" @click="adjustNumber(setting, 'maxCoperti', -1)">-</button>
                <input 
                  type="number" 
                  min="0" 
                  v-model.number="setting.maxCoperti" 
                  @blur="updateSetting(setting)" 
                  class="stepper-value" 
                />
                <button type="button" class="stepper-btn" @click="adjustNumber(setting, 'maxCoperti', 1)">+</button>
              </div>
              <small class="field-hint">Limite prenotazioni online simultanee</small>
            </div>

            <div class="covers-field">
              <label class="field-label">
                <i class="fas fa-chair me-1 text-primary"></i> Max Coperti Singolo Tavolo
              </label>
              <div class="stepper-input">
                <button type="button" class="stepper-btn" @click="adjustNumber(setting, 'maxCopertiTavolo', -1)">-</button>
                <input 
                  type="number" 
                  min="0" 
                  v-model.number="setting.maxCopertiTavolo" 
                  @blur="updateSetting(setting)" 
                  class="stepper-value" 
                />
                <button type="button" class="stepper-btn" @click="adjustNumber(setting, 'maxCopertiTavolo', 1)">+</button>
              </div>
              <small class="field-hint">Capienza massima per prenotazione</small>
            </div>
          </div>

          <!-- Shifts Container: Pranzo & Cena -->
          <div class="shifts-container">
            <!-- Servizio Pranzo -->
            <div class="shift-card lunch-shift" :class="{ 'shift-inactive': setting.pranzoAttivo === false }">
              <div class="shift-header">
                <div class="shift-title-box">
                  <span class="shift-icon-badge lunch-badge">
                    <i class="fas fa-sun"></i>
                  </span>
                  <span class="shift-title">Servizio Pranzo</span>
                </div>
                <div class="sci-toggle">
                  <input 
                    type="checkbox" 
                    :id="'toggle-pranzo-' + setting.codicesala" 
                    v-model="setting.pranzoAttivo" 
                    @change="updateSetting(setting)"
                  />
                  <span class="sci-toggle-slider"></span>
                </div>
              </div>

              <div class="shift-body" v-if="setting.pranzoAttivo !== false">
                <div class="time-range-inputs">
                  <div class="time-group">
                    <label class="time-label">Dalle ore</label>
                    <input 
                      type="time" 
                      v-model="setting.pranzoStart" 
                      @change="updateSetting(setting)"
                      class="sci-time-input" 
                    />
                  </div>
                  <div class="time-separator">
                    <i class="fas fa-arrow-right"></i>
                  </div>
                  <div class="time-group">
                    <label class="time-label">Alle ore</label>
                    <input 
                      type="time" 
                      v-model="setting.pranzoEnd" 
                      @change="updateSetting(setting)"
                      class="sci-time-input" 
                    />
                  </div>
                </div>

                <!-- Preset Rapidi Pranzo -->
                <div class="presets-row">
                  <span class="preset-label">Preset:</span>
                  <button type="button" class="preset-chip" @click="applyPreset(setting, 'pranzo', '12:00', '14:30')">12:00-14:30</button>
                  <button type="button" class="preset-chip" @click="applyPreset(setting, 'pranzo', '12:30', '15:00')">12:30-15:00</button>
                </div>
              </div>
              <div class="shift-disabled-hint" v-else>
                <i class="fas fa-pause-circle me-1"></i> Prenotazioni a pranzo disattivate per questa sala
              </div>
            </div>

            <!-- Servizio Cena -->
            <div class="shift-card dinner-shift" :class="{ 'shift-inactive': setting.cenaAttivo === false }">
              <div class="shift-header">
                <div class="shift-title-box">
                  <span class="shift-icon-badge dinner-badge">
                    <i class="fas fa-moon"></i>
                  </span>
                  <span class="shift-title">Servizio Cena</span>
                </div>
                <div class="sci-toggle">
                  <input 
                    type="checkbox" 
                    :id="'toggle-cena-' + setting.codicesala" 
                    v-model="setting.cenaAttivo" 
                    @change="updateSetting(setting)"
                  />
                  <span class="sci-toggle-slider"></span>
                </div>
              </div>

              <div class="shift-body" v-if="setting.cenaAttivo !== false">
                <div class="time-range-inputs">
                  <div class="time-group">
                    <label class="time-label">Dalle ore</label>
                    <input 
                      type="time" 
                      v-model="setting.cenaStart" 
                      @change="updateSetting(setting)"
                      class="sci-time-input" 
                    />
                  </div>
                  <div class="time-separator">
                    <i class="fas fa-arrow-right"></i>
                  </div>
                  <div class="time-group">
                    <label class="time-label">Alle ore</label>
                    <input 
                      type="time" 
                      v-model="setting.cenaEnd" 
                      @change="updateSetting(setting)"
                      class="sci-time-input" 
                    />
                  </div>
                </div>

                <!-- Preset Rapidi Cena -->
                <div class="presets-row">
                  <span class="preset-label">Preset:</span>
                  <button type="button" class="preset-chip" @click="applyPreset(setting, 'cena', '19:00', '23:00')">19:00-23:00</button>
                  <button type="button" class="preset-chip" @click="applyPreset(setting, 'cena', '19:30', '23:30')">19:30-23:30</button>
                  <button type="button" class="preset-chip" @click="applyPreset(setting, 'cena', '20:00', '00:00')">20:00-00:00</button>
                </div>
              </div>
              <div class="shift-disabled-hint" v-else>
                <i class="fas fa-pause-circle me-1"></i> Prenotazioni a cena disattivate per questa sala
              </div>
            </div>
          </div>
        </div>

        <!-- Card Footer -->
        <div class="room-card-footer">
          <div class="save-status">
            <span v-if="savingMap[setting.codicesala]" class="text-primary">
              <i class="fas fa-spinner fa-spin me-1"></i>Salvataggio...
            </span>
            <span v-else-if="savedMap[setting.codicesala]" class="text-success">
              <i class="fas fa-check-circle me-1"></i>Salvato
            </span>
            <span v-else class="text-muted">
              <i class="fas fa-shield-alt me-1"></i>Pronto
            </span>
          </div>
          <button 
            type="button" 
            class="sci-btn sci-btn-primary btn-sm btn-touch" 
            @click="updateSetting(setting, true)"
          >
            <i class="fas fa-save me-1"></i>Salva Sala
          </button>
        </div>
      </div>
    </div>

    <!-- Toast Feedback Temporaneo -->
    <transition name="fade">
      <div v-if="toastMessage" class="feedback-toast" :class="toastType">
        <i :class="toastType === 'success' ? 'fas fa-check-circle' : 'fas fa-info-circle'" class="me-2"></i>
        {{ toastMessage }}
      </div>
    </transition>
  </div>
</template>

<script>
import { store } from '../store/store.js'
import newOrderSound from '@/assets/newOrder.mp3'

export default {
  name: 'PrenotazioniSetting',
  data() {
    return {
      settings: [],
      loading: true,
      isSavingAll: false,
      savingMap: {},
      savedMap: {},
      toastMessage: '',
      toastType: 'success',
      toastTimer: null,
      audioPlayer: null,
      store
    }
  },
  computed: {
    activeRoomsCount() {
      return this.settings.filter(s => s.attiva !== false).length;
    },
    totalCapacity() {
      return this.settings.reduce((sum, s) => sum + (Number(s.maxCoperti) || 0), 0);
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (parsed.token || localStorage.token);
      } catch (e) {
        return localStorage.token || '';
      }
    },
    fetchSettings() {
      this.loading = true;
      const token = this.getToken();
      this.axios.get(this.store.path + 'prenotazioni-setting', {
        headers: { authorization: `Bearer ${token}` }
      }).then(response => {
        if (Array.isArray(response.data)) {
          this.settings = response.data
            .filter(setting => setting.codicesala && setting.codicesala.toLowerCase() !== 'asporto')
            .map(s => ({
              ...s,
              attiva: s.attiva !== undefined ? s.attiva : true,
              pranzoAttivo: s.pranzoAttivo !== undefined ? s.pranzoAttivo : true,
              cenaAttivo: s.cenaAttivo !== undefined ? s.cenaAttivo : true,
              maxCoperti: Number(s.maxCoperti) || 0,
              maxCopertiTavolo: Number(s.maxCopertiTavolo) || 0,
              pranzoStart: s.pranzoStart || '12:00',
              pranzoEnd: s.pranzoEnd || '14:30',
              cenaStart: s.cenaStart || '19:30',
              cenaEnd: s.cenaEnd || '23:30'
            }));
        }
      }).catch(error => {
        console.error("Error fetching settings:", error);
        this.showToast("Errore nel caricamento delle impostazioni", "error");
      }).finally(() => {
        this.loading = false;
      });
    },
    adjustNumber(setting, field, delta) {
      const current = Number(setting[field]) || 0;
      const next = Math.max(0, current + delta);
      setting[field] = next;
      this.updateSetting(setting);
    },
    applyPreset(setting, shift, start, end) {
      if (shift === 'pranzo') {
        setting.pranzoStart = start;
        setting.pranzoEnd = end;
        setting.pranzoAttivo = true;
      } else {
        setting.cenaStart = start;
        setting.cenaEnd = end;
        setting.cenaAttivo = true;
      }
      this.updateSetting(setting);
    },
    updateSetting(setting, showToast = false) {
      const token = this.getToken();
      const salaKey = setting.codicesala;
      this.$set ? this.$set(this.savingMap, salaKey, true) : (this.savingMap[salaKey] = true);

      this.axios.post(this.store.path + 'prenotazioni-setting', setting, {
        headers: { authorization: `Bearer ${token}` }
      }).then(() => {
        if (this.$set) {
          this.$set(this.savingMap, salaKey, false);
          this.$set(this.savedMap, salaKey, true);
        } else {
          this.savingMap[salaKey] = false;
          this.savedMap[salaKey] = true;
        }

        if (showToast) {
          this.showToast(`Impostazioni per ${setting.nomeSala || setting.codicesala} salvate con successo!`);
        }

        setTimeout(() => {
          if (this.$set) this.$set(this.savedMap, salaKey, false);
          else this.savedMap[salaKey] = false;
        }, 2500);
      }).catch(error => {
        console.error("Error updating setting:", error);
        if (this.$set) this.savingMap[salaKey] = false;
        else this.savingMap[salaKey] = false;
        this.showToast("Errore durante il salvataggio della sala", "error");
      });
    },
    async saveAllSettings() {
      this.isSavingAll = true;
      try {
        for (const s of this.settings) {
          await this.updateSetting(s);
        }
        this.showToast("Tutte le impostazioni delle sale sono state salvate!");
      } catch (err) {
        console.error("Errore salvataggio multiplo:", err);
      } finally {
        this.isSavingAll = false;
      }
    },
    testNotificationSound() {
      try {
        if (!this.audioPlayer) {
          this.audioPlayer = new Audio(newOrderSound);
        }
        this.audioPlayer.currentTime = 0;
        const promise = this.audioPlayer.play();
        if (promise !== undefined) {
          promise.catch(() => {
            this.playWebAudioChime();
          });
        }
      } catch (e) {
        this.playWebAudioChime();
      }
      this.playWebAudioChime();
      this.showToast("🔔 Suono notifica riprodotto con successo!");
    },
    playWebAudioChime() {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const playTone = (freq, startTime, duration) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, startTime);
          gain.gain.setValueAtTime(0.35, startTime);
          gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(startTime);
          osc.stop(startTime + duration);
        };
        const now = ctx.currentTime;
        playTone(659.25, now, 0.4);       // E5
        playTone(880.00, now + 0.15, 0.6); // A5
      } catch (err) {
        console.warn("Web Audio fallback error:", err);
      }
    },
    showToast(message, type = 'success') {
      this.toastMessage = message;
      this.toastType = type;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastMessage = '';
      }, 3500);
    },
    home() {
      this.$router.push({ name: 'Gestionale' });
    }
  },
  mounted() {
    this.fetchSettings();
  }
}
</script>

<style scoped>
.sci-view-container {
  min-height: 100vh;
  background-color: #f1f5f9;
  padding: 24px 32px 60px 32px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #0f172a;
  box-sizing: border-box;
}

/* Header */
.sci-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sci-btn-back:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-badge-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #0284c7;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.sci-view-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}

.sci-view-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 4px 0 0 0;
}

.sci-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* Bento KPI Grid */
.sci-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.sci-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sci-kpi-card.primary {
  border-left: 4px solid #0284c7;
}

.kpi-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.kpi-icon-wrap.blue { background: #eff6ff; color: #2563eb; }
.kpi-icon-wrap.green { background: #f0fdf4; color: #16a34a; }
.kpi-icon-wrap.purple { background: #faf5ff; color: #7c3aed; }

.kpi-meta {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.kpi-value {
  font-size: 1.6rem;
  font-weight: 800;
  color: #0f172a;
  margin: 2px 0;
}

.kpi-sub {
  font-size: 0.75rem;
  color: #94a3b8;
}

.text-success { color: #16a34a !important; }

/* Existing Styles */

/* Top Bar */
.settings-top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.top-bar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-titles {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--sci-text-muted, #64748b);
}

.breadcrumbs .separator {
  font-size: 10px;
  color: var(--sci-border, #cbd5e1);
}

.breadcrumbs .current {
  color: var(--sci-primary, #4f46e5);
  font-weight: 600;
}

.page-title {
  font-size: 24px;
  font-weight: 800;
  color: var(--sci-text-main, #0f172a);
  margin: 0;
  letter-spacing: -0.02em;
}

.top-bar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.test-sound-btn {
  background: var(--sci-amber-subtle, #fef3c7);
  color: var(--sci-amber, #d97706);
  border: 1px solid var(--sci-amber-border, #fde68a);
  font-weight: 700;
}

.test-sound-btn:hover {
  background: #fde68a;
  color: #92400e;
}

/* Info Banner */
.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-left: 5px solid var(--sci-primary, #3b82f6);
  border-radius: var(--radius-md, 14px);
  margin-bottom: 28px;
}

.info-banner-icon {
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 10px;
  background: #dbeafe;
  color: #1d4ed8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.info-banner-content {
  flex: 1;
}

.info-banner-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e40af;
  margin: 0 0 4px 0;
}

.info-banner-desc {
  font-size: 13.5px;
  color: #1e3a8a;
  margin: 0;
  line-height: 1.5;
}

/* Bento Grid */
.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
  gap: 24px;
}

@media (max-width: 768px) {
  .settings-grid {
    grid-template-columns: 1fr;
  }
}

.room-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: var(--radius-lg, 18px);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.05);
  transition: all 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
  overflow: hidden;
}

.room-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08);
  transform: translateY(-2px);
}

.card-disabled {
  opacity: 0.75;
  background: #fafafa;
}

/* Room Card Header */
.room-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid var(--sci-border, #f1f5f9);
  background: #fafbfd;
}

.room-title-box {
  display: flex;
  align-items: center;
  gap: 14px;
}

.room-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #e2e8f0;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s ease;
}

.room-icon-wrapper.icon-active {
  background: #e0e7ff;
  color: var(--sci-primary, #4f46e5);
}

.room-name {
  font-size: 18px;
  font-weight: 800;
  color: var(--sci-text-main, #0f172a);
  margin: 0 0 2px 0;
  text-transform: capitalize;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.pill-active {
  background: #ecfdf5;
  color: #065f46;
}

.pill-active .status-dot {
  background: #10b981;
}

.pill-inactive {
  background: #f1f5f9;
  color: #64748b;
}

.pill-inactive .status-dot {
  background: #94a3b8;
}

.master-switch-box {
  display: flex;
  align-items: center;
}

.toggle-switch-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin: 0;
}

.toggle-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--sci-text-muted, #64748b);
}

/* Sci Toggle Switch (W3C Accessible) */
.sci-toggle {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 26px;
}

.sci-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.sci-toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: .3s cubic-bezier(0.2, 0.8, 0.2, 1);
  border-radius: 34px;
}

.sci-toggle-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .3s cubic-bezier(0.2, 0.8, 0.2, 1);
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.15);
}

.sci-toggle input:checked + .sci-toggle-slider {
  background-color: var(--sci-primary, #4f46e5);
}

.sci-toggle input:checked + .sci-toggle-slider:before {
  transform: translateX(20px);
}

/* Card Body */
.room-card-body {
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}

/* Covers Section */
.covers-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--sci-border, #f1f5f9);
}

.covers-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--sci-text-main, #334155);
  margin: 0;
}

.field-hint {
  font-size: 11px;
  color: var(--sci-text-muted, #94a3b8);
}

/* Stepper Input */
.stepper-input {
  display: flex;
  align-items: center;
  border: 1px solid var(--sci-border, #cbd5e1);
  border-radius: 10px;
  overflow: hidden;
  background: #f8fafc;
  height: 42px;
}

.stepper-btn {
  width: 38px;
  height: 100%;
  border: none;
  background: transparent;
  font-size: 18px;
  font-weight: 700;
  color: #475569;
  cursor: pointer;
  transition: background 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stepper-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.stepper-value {
  flex: 1;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  outline: none;
  -moz-appearance: textfield;
}

.stepper-value::-webkit-inner-spin-button,
.stepper-value::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Shifts Container */
.shifts-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.shift-card {
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: var(--radius-md, 14px);
  padding: 14px 16px;
  background: #ffffff;
  transition: all 0.2s ease;
}

.lunch-shift {
  border-left: 4px solid #f59e0b;
}

.dinner-shift {
  border-left: 4px solid #6366f1;
}

.shift-inactive {
  opacity: 0.6;
  background: #f8fafc;
}

.shift-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.shift-title-box {
  display: flex;
  align-items: center;
  gap: 8px;
}

.shift-icon-badge {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.lunch-badge {
  background: #fef3c7;
  color: #d97706;
}

.dinner-badge {
  background: #e0e7ff;
  color: #4f46e5;
}

.shift-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.time-range-inputs {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.time-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.time-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin: 0;
}

.sci-time-input {
  width: 100%;
  height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  background: #f8fafc;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.sci-time-input:focus {
  border-color: #4f46e5;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
}

.time-separator {
  color: #94a3b8;
  font-size: 14px;
  padding-top: 16px;
}

.presets-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.preset-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
}

.preset-chip {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11.5px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease;
}

.preset-chip:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.shift-disabled-hint {
  font-size: 12px;
  color: #64748b;
  font-style: italic;
  padding: 6px 0;
}

/* Card Footer */
.room-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 22px;
  background: #fafbfd;
  border-top: 1px solid var(--sci-border, #f1f5f9);
}

.save-status {
  font-size: 12px;
  font-weight: 600;
}

/* Toast Feedback */
.feedback-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 14px 22px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  z-index: 99999;
  display: flex;
  align-items: center;
}

.feedback-toast.success {
  background: #065f46;
  color: #ffffff;
}

.feedback-toast.error {
  background: #991b1b;
  color: #ffffff;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .3s, transform .3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
